import { useEffect, useState } from 'react';
import { Card, Descriptions, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { fetchBackendBuildInfo } from '@/apis/build';
import { frontendBuildInfo, type BuildInfo } from '@/common/buildInfo';
import { parseDevConnectionInfo, type DevConnectionInfo } from '@/common/devConnection';
import { LOCAL_DEV_AUTH } from '@/common/auth/localDevAuth';

const { Title, Text } = Typography;

const formatTime = (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-');

const rowItems = (info: BuildInfo) => [
  { key: 'version', label: '版本', children: <Tag color="purple">v{info.version}</Tag> },
  { key: 'branch', label: '分支', children: <Text code>{info.branch}</Text> },
  {
    key: 'commit',
    label: 'Commit',
    children: (
      <Space size={8}>
        <Text code>{info.commitShort}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>{info.commit}</Text>
      </Space>
    ),
  },
  {
    key: 'subject',
    label: 'Commit 说明',
    children: info.commitSubject ? <Text>{info.commitSubject}</Text> : <Text type="secondary">-</Text>,
  },
  { key: 'commitTime', label: '提交时间', children: formatTime(info.commitTime) },
  { key: 'buildTime', label: '打包时间', children: formatTime(info.buildTime) },
  { key: 'builtBy', label: '打包人', children: info.builtBy },
];

// DEV 分支在生产构建中消除，连接配置只从本地开发服务器读取。
const DevConnectionCard = import.meta.env.DEV ? function DevConnectionCard() {
  const [info, setInfo] = useState<DevConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const load = async () => {
      try {
        const response = await fetch('/__dev/connection-info', {
          method: 'GET', mode: 'same-origin', redirect: 'error', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          if (active) setError(`本地开发连接信息读取失败（HTTP ${response.status}）。`);
          return;
        }
        const parsed = parseDevConnectionInfo(await response.json());
        if (active) setInfo(parsed);
      } catch {
        if (active && !controller.signal.aborted) setError('无法读取本地开发连接信息，或接口返回格式不正确。请确认正在使用 Vite 开发服务器。');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; controller.abort(); };
  }, []);

  return <Card size="small" title="本地开发连接" bordered style={{ marginTop: 16 }}>
    {loading ? <Spin /> : error ? <Text type="danger" role="alert">{error}</Text> : info && <Descriptions column={1} size="small" items={[
      { key: 'origin', label: '本机浏览器 Origin', children: <Text code>{window.location.origin}</Text> },
      { key: 'mode', label: '开发配置模式', children: <Text code>{info.mode}</Text> },
      { key: 'backendLabel', label: '数据后端标签', children: info.backendLabel || '未标注' },
      { key: 'auth', label: '访问身份', children: LOCAL_DEV_AUTH ? <Tag color="cyan">本机免登录 Mock · 后端 local-dev 身份</Tag> : '正常登录会话' },
      { key: 'apiBase', label: '浏览器 API 前缀 / 地址', children: <Text code>{info.apiBase || '（空前缀）'}</Text> },
      { key: 'requestMode', label: '请求模式', children: <Text code>{info.requestMode}</Text> },
      { key: 'proxyTarget', label: '实际代理目标', children: info.proxyTarget === null ? '无代理目标' : <Text code>{info.proxyTarget}</Text> },
      { key: 'verifyProxyTLS', label: '代理 TLS 校验', children: info.verifyProxyTLS ? '启用' : '关闭' },
      { key: 'host', label: '监听地址', children: <Text code>{info.listen.host}</Text> },
      { key: 'port', label: '监听端口', children: info.listen.port },
      { key: 'strictPort', label: '固定端口（strictPort）', children: info.listen.strictPort ? '是' : '否' },
      { key: 'configurationFiles', label: '配置文件', children: info.configurationFiles.length
        ? <Space direction="vertical" size={0}>{info.configurationFiles.map((file, index) => <Text code key={index}>{file}</Text>)}</Space> : '无' },
      { key: 'sources', label: '配置来源', children: Object.keys(info.sources).length
        ? <Space direction="vertical" size={0}>{Object.entries(info.sources).map(([key, source]) => <span key={key}><Text code>{key}</Text>：{source}</span>)}</Space> : '未提供' },
      { key: 'overrides', label: '覆盖项', children: info.overrides.length
        ? <Space direction="vertical" size={0}>{info.overrides.map((override, index) => <Text key={index}>{override}</Text>)}</Space> : '无' },
    ]} />}
  </Card>;
} : null;

/**
 * 系统 / 关于:并列展示后端 + 前端的构建元信息(版本 / 分支 / commit / 打包时间 / 打包人)。
 * 后端来自 /api/build-info,前端来自 vite build 前 gen-build-info.mjs 冻结的 json。
 */
const AboutPage = () => {
  const [backend, setBackend] = useState<BuildInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchBackendBuildInfo().then((info) => {
      if (!active) return;
      setBackend(info);
      setLoading(false);
    }).catch(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 4 }}>关于</Title>
      <Text type="secondary">仿真管理平台 · 前后端构建元信息</Text>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
        <Card size="small" title="后端 (Backend)" bordered>
          {loading ? (
            <Spin />
          ) : backend ? (
            <Descriptions column={1} size="small" items={rowItems(backend)} />
          ) : (
            <Text type="secondary">未能获取后端构建信息 (可能未部署或未生成 build_info.json)。</Text>
          )}
        </Card>

        <Card size="small" title="前端 (Frontend)" bordered>
          <Descriptions column={1} size="small" items={rowItems(frontendBuildInfo)} />
        </Card>
      </div>
      {DevConnectionCard && <DevConnectionCard />}
    </div>
  );
};

export default AboutPage;
